import { Link } from "react-router-dom";
import { ArrowRight, Mic, MessageCircle, Mail, Search, Plug, Bot } from "lucide-react";
import { Layout } from "../components/Layout";
import { SectionHeader, FeatureCard, GreenCta } from "../components/Shared";
import { SeoText } from "../components/SeoText";
import { useSeo } from "../hooks/useSeo";
import { WHATSAPP_LINK } from "../lib/api";

const services = [
  { num: "01", Icon: Mic, title: "Réceptionniste Vocal IA", text: "Un agent vocal qui répond à vos appels 24h/24, qualifie les prospects et place les rendez-vous dans votre agenda.", to: "/services/receptionniste-ia" },
  { num: "02", Icon: MessageCircle, title: "Chatbot WhatsApp", text: "Répondez instantanément aux messages clients. Votre assistant IA traite les demandes courantes jour et nuit.", to: "/services/whatsapp-ia" },
  { num: "03", Icon: Mail, title: "Pipeline Email IA", text: "Tri intelligent, brouillons rédigés automatiquement, relances programmées. Vos heures email divisées par trois.", to: "/contact" },
  { num: "04", Icon: Plug, title: "Intégration Systèmes", text: "Je connecte vos outils existants (CRM, comptabilité, agenda, Drive) pour qu'ils se parlent enfin.", to: "/contact" },
  { num: "05", Icon: Search, title: "Audit IA gratuit", text: "60 minutes pour identifier vos 3 processus les plus automatisables. Plan d'action écrit fourni.", to: "/audit-ia" },
  { num: "06", Icon: Bot, title: "Agent Sur Mesure", text: "Un workflow unique à votre activité ? Je le conçois, le construis et le déploie sur vos outils.", to: "/contact" },
];

const Services = () => {
  useSeo({
    title: "Services d'Automatisation IA pour PME — Genève",
    description: "Réceptionniste vocal IA, chatbot WhatsApp, automatisation administrative : des solutions concrètes pour les PME genevoises, sans compétences techniques requises.",
    path: "/services",
  });

  return (
    <Layout>
      {/* HERO */}
      <section className="px-6 pt-14 pb-12 md:pt-24 text-center">
        <div className="max-w-4xl mx-auto fade-in">
          <span className="pill-badge mb-6">Services</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold title-text leading-[1.05] tracking-tight">
            Des outils IA concrets pour <span className="accent-text">automatiser votre PME</span>
          </h1>
          <p className="body-text text-lg mt-7 leading-relaxed max-w-2xl mx-auto">
            Six systèmes pensés pour les PME suisses. Aucun jargon technique, déploiement
            en 30 jours garantis, données hébergées en Suisse.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row gap-4 justify-center flex-wrap">
            <Link to="/audit-ia" className="btn-primary">
              Réserver un audit gratuit <ArrowRight size={18} />
            </Link>
            <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="btn-secondary">
              <MessageCircle size={18} /> WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* GRID */}
      <section className="px-6 py-16">
        <div className="max-w-6xl mx-auto">
          <SectionHeader badge="Nos services" title="Six systèmes." accentTitle="Un seul objectif." subtitle="Vous faire gagner des heures chaque semaine." />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((s) => (
              <Link key={s.num} to={s.to} className="card-base fade-in group" data-testid={`services-grid-${s.num}`}>
                <div className="flex items-center justify-between mb-5">
                  <span className="icon-square">
                    <s.Icon size={24} />
                  </span>
                  <span className="text-3xl font-extrabold title-text opacity-20">{s.num}</span>
                </div>
                <h3 className="title-text text-lg font-bold mb-2">{s.title}</h3>
                <p className="body-text text-sm leading-relaxed">{s.text}</p>
                <span className="mt-5 inline-flex items-center gap-1 accent-text text-sm font-semibold group-hover:gap-2 transition-all">
                  Découvrir <ArrowRight size={14} />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <GreenCta
        badge="Prochaine étape"
        title="Trouvez le bon levier en 60 minutes."
        subtitle="L'audit IA gratuit identifie pour vous les 3 services qui ont le meilleur ROI dans votre activité."
        primaryLabel={<>Réserver mon audit <ArrowRight size={18} /></>}
        primaryAction={() => (window.location.href = "/audit-ia")}
        secondaryLabel="WhatsApp"
        secondaryAction={WHATSAPP_LINK}
        footnotes={["Gratuit", "Sans engagement", "Basé à Genève"]}
      />

      {/* SEO TEXT */}
      <SeoText>
        <h2 className="section-title mb-6">
          Solutions d'<span className="accent-text">automatisation IA</span> sur mesure
        </h2>
        <p className="body-text leading-relaxed mb-5">
          Mes services s'adressent aux PME et indépendants de Genève et de Suisse romande
          qui veulent gagner du temps, capter plus d'opportunités et standardiser leurs
          processus sans recruter. Chaque solution proposée a été testée dans mes propres
          entreprises avant d'arriver chez vous — c'est ma garantie principale : je ne vends
          pas de l'IA en théorie, je déploie des systèmes qui tournent en production.
        </p>
        <p className="body-text leading-relaxed mb-5">
          Le <strong>réceptionniste vocal IA</strong> remplace votre standard téléphonique
          ou votre secrétariat externalisé. Il décroche dès la première sonnerie, comprend
          le besoin du client, propose un créneau dans votre agenda, envoie un SMS de
          confirmation et vous transmet un résumé. Idéal pour les fiduciaires, cabinets
          médicaux, agences immobilières, garages, parkings et services à domicile qui
          perdent du chiffre d'affaires à chaque appel manqué.
        </p>
        <p className="body-text leading-relaxed mb-5">
          Le <strong>chatbot WhatsApp</strong> traite automatiquement les questions
          fréquentes de vos clients : disponibilités, tarifs, horaires, suivi de commande,
          prise de rendez-vous. Le client reste sur WhatsApp, il n'a aucune application
          à installer, et vos équipes ne sont sollicitées que pour les vrais cas qui
          demandent un humain. Mise en place en 24h sur votre numéro existant.
        </p>
        <p className="body-text leading-relaxed mb-5">
          Le <strong>pipeline email IA</strong> trie automatiquement votre boîte de réception,
          identifie les urgences, prépare des brouillons de réponse, programme les relances
          commerciales et archive ce qui ne nécessite pas d'action. Les dirigeants que
          j'accompagne récupèrent en moyenne 6 à 10 heures par semaine sur leur courrier
          électronique professionnel.
        </p>
        <p className="body-text leading-relaxed mb-5">
          Pour les besoins plus spécifiques, je conçois des <strong>agents sur mesure</strong>{" "}
          et j'<strong>intègre vos systèmes existants</strong> (Google Workspace, Microsoft 365,
          Bexio, Klara, Salesforce, HubSpot, etc.) pour qu'ils communiquent en temps réel.
          Si un processus se répète plusieurs fois par semaine dans votre entreprise, il
          peut probablement être automatisé.
        </p>
        <p className="body-text leading-relaxed">
          Tous mes déploiements respectent la LPD et le FADP, vos données restent hébergées
          en Suisse, et je m'engage sur un délai maximum de 30 jours entre la signature et
          la mise en production. Réservez votre audit gratuit pour découvrir le service le
          plus adapté à votre activité.
        </p>
      </SeoText>
    </Layout>
  );
};

export default Services;
